import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, EmptyState, Modal } from '../components/common';
import { AttributePanel } from '../components/attributes/AttributePanel';
import { Composer } from '../components/chat/Composer';
import { MessageList } from '../components/chat/MessageList';
import { AppShell } from '../components/layout/AppShell';
import { Sidebar } from '../components/layout/Sidebar';
import { TopBar } from '../components/layout/TopBar';
import { useModel } from '../context/ModelContext';
import { usePrompts } from '../context/PromptContext';
import { useAttributeSet } from '../hooks/useAttributeSet';
import { useChat } from '../hooks/useChat';
import { useConversations } from '../hooks/useConversations';
import { useImageUpload } from '../hooks/useImageUpload';
import { useJobList } from '../hooks/useBatchJob';
import { useWideLayout } from '../hooks/useMediaQuery';

/**
 * The main screen: conversations, thread, composer, attribute inspector.
 *
 * The inspector is a third column on a wide display and a drawer below 1500px —
 * a 41-row table and a chat thread both need width, and shrinking both to fit is
 * worse than moving one behind a button.
 */
export function ChatPage() {
  const { conversationId: routeConversationId } = useParams();
  const navigate = useNavigate();
  const wide = useWideLayout();

  const {
    activeVersion,
    activeId,
    needsSetup,
    inferenceAlive,
    inferenceProblem,
    loading: modelLoading,
  } = useModel();
  const { selected: selectedPrompt } = usePrompts();
  const conversationsState = useConversations();
  const { jobs } = useJobList();
  const attachments = useImageUpload({ max: 4 });

  const [attributeMode, setAttributeMode] = useState(true);
  const [inspectorOpen, setInspectorOpen] = useState(false);

  const chat = useChat({
    onConversationCreated: (conversation) => {
      conversationsState.refresh();
      if (conversation?.id) navigate(`/c/${conversation.id}`, { replace: true });
    },
  });

  const { attributeSet } = useAttributeSet(activeVersion?.attribute_set);

  useEffect(() => {
    if (routeConversationId && routeConversationId !== chat.conversationId) {
      chat.loadConversation(routeConversationId);
    }
    if (!routeConversationId && chat.conversationId) {
      chat.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeConversationId]);

  const startNew = useCallback(() => {
    chat.reset();
    attachments.clear();
    navigate('/');
  }, [attachments, chat, navigate]);

  const send = useCallback(
    async ({ promptText, promptId, mode }) => {
      const previewImages = attachments.items.map((item) => ({
        id: item.id,
        filename: item.filename,
        previewUrl: item.previewUrl,
      }));

      await chat.send({
        image_ids: attachments.imageIds,
        version_id: activeId || undefined,
        prompt_id: promptId || undefined,
        prompt_text: promptText || undefined,
        mode,
        displayText: promptText || (mode === 'extraction' ? 'Extract all attributes' : undefined),
        previewImages,
      });

      // A new analysis is not a question about an earlier picture, so the selection
      // does not survive one. Leaving it set would aim the *next* follow-up at an
      // image the user stopped looking at several turns ago.
      if (attachments.imageIds.length) chat.setTarget(null);

      attachments.clear();
      conversationsState.refresh();

      // Once attributes exist, the next message is almost always a question about
      // them, so extraction stops being the default. The toggle stays visible and
      // one click away — nothing is hidden, only re-defaulted.
      if (mode === 'extraction') setAttributeMode(false);
    },
    [activeId, attachments, chat, conversationsState],
  );

  const followUp = useCallback(
    (question) => send({ promptText: question, promptId: null, mode: 'chat' }),
    [send],
  );

  const lastAssistantWithAttributes = useMemo(
    () =>
      [...chat.messages]
        .reverse()
        .find((message) => message.role === 'assistant' && message.attributes) || null,
    [chat.messages],
  );

  const sentPrompt = useMemo(() => {
    const index = chat.messages.findIndex(
      (message) => message.id === lastAssistantWithAttributes?.id,
    );
    for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
      if (chat.messages[cursor].role === 'user') return chat.messages[cursor].sent_prompt;
    }
    return '';
  }, [chat.messages, lastAssistantWithAttributes]);

  const groundingLabel = chat.groundingImages[0]?.filename || '';

  const composerDisabled = needsSetup || !activeVersion || !inferenceAlive;
  const disabledReason = needsSetup
    ? 'No version is ready — add checkpoint metadata in Settings.'
    : !activeVersion
      ? 'Select a model version first.'
      : !inferenceAlive
        ? inferenceProblem
        : '';

  const panel = (
    <AttributePanel
      attributes={chat.attributes}
      message={lastAssistantWithAttributes}
      sentPrompt={sentPrompt}
      onClose={wide ? null : () => setInspectorOpen(false)}
    />
  );

  return (
    <AppShell
      sidebar={
        <Sidebar
          conversations={conversationsState.conversations}
          loading={conversationsState.loading}
          activeConversationId={chat.conversationId}
          onNewAnalysis={startNew}
          onSelectConversation={(id) => navigate(`/c/${id}`)}
          onDeleteConversation={async (id) => {
            await conversationsState.remove(id);
            if (id === chat.conversationId) startNew();
          }}
          onPinConversation={conversationsState.setPinned}
          runningJobs={jobs.filter((job) => job.status === 'running').length}
        />
      }
      inspector={wide ? panel : null}
    >
      <main className="page">
        <TopBar
          title={
            chat.messages.length
              ? conversationsState.conversations.find(
                  (conversation) => conversation.id === chat.conversationId,
                )?.title || 'Analysis'
              : 'New analysis'
          }
        >
          {!wide && chat.attributes && (
            <button
              type="button"
              className="chip chip--button"
              onClick={() => setInspectorOpen(true)}
            >
              ▤ Attributes {chat.attributes.counts?.ok ?? 0}/{chat.attributes.attribute_count ?? 0}
            </button>
          )}
        </TopBar>

        {chat.messages.length === 0 && !chat.streaming ? (
          <div className="grow" style={{ display: 'grid', placeItems: 'center' }}>
            {modelLoading ? null : needsSetup ? (
              <EmptyState glyph="◧" title="No model version is ready">
                Import a checkpoint and give it an attribute set, and this screen becomes usable.
                <div style={{ marginTop: 14 }}>
                  <Button variant="primary" onClick={() => navigate('/settings/versions')}>
                    Open model versions
                  </Button>
                </div>
              </EmptyState>
            ) : (
              <EmptyState glyph="▦" title="Drop a road image to start">
                {attributeSet
                  ? `${activeVersion?.label || activeVersion?.id} will code all ${attributeSet.attribute_count} iRAP attributes, then you can cross-question it about any of them.`
                  : 'Attach an image and ask a question, or turn on attribute extraction.'}
              </EmptyState>
            )}
          </div>
        ) : (
          <MessageList
            messages={chat.messages}
            pending={chat.pending}
            streaming={chat.streaming}
            streamText={chat.streamText}
            versionId={activeId}
            onFollowUp={followUp}
            onOpenAttributes={() => setInspectorOpen(true)}
            images={chat.images}
          />
        )}

        <Composer
          onSend={send}
          onStop={chat.stop}
          streaming={chat.streaming}
          attachments={attachments.items}
          onFiles={attachments.addFiles}
          onRemoveAttachment={attachments.remove}
          attributeMode={attributeMode}
          onAttributeModeChange={setAttributeMode}
          disabled={composerDisabled}
          disabledReason={disabledReason}
          hasGroundingImage={chat.hasImage}
          groundingLabel={groundingLabel}
          attributeCount={attributeSet?.attribute_count || 0}
          images={chat.images}
          target={chat.target}
          onTargetChange={chat.setTarget}
          resolvedTarget={chat.resolvedTarget}
        />
      </main>

      {!wide && (
        <Modal
          open={inspectorOpen}
          onClose={() => setInspectorOpen(false)}
          title="Structured attributes"
          subtitle={selectedPrompt ? `Prompt: ${selectedPrompt.name}` : undefined}
          wide
        >
          <div style={{ height: '70vh', display: 'flex' }}>{panel}</div>
        </Modal>
      )}
    </AppShell>
  );
}
